import * as React from 'react';
import { Route, Routes, useNavigate, useLocation, } from "react-router-dom-v5-compat";
import queryString from 'query-string';

import LockedPage from './locked_page'
import Sidebar from './sidebar'
import SmartGroups from './smart_groups';
import PerformanceBreakdown from './performance_breakdown';
import StandardsReportHeader from './standards_report_header'
import StandardsReportSubheader from './standards_report_subheader';
import { ClassroomInterface, SkillCategoryInterface, StudentPerformanceData, StateStandardInterface, StateStandardPerformanceData, StateStandardsFrameworkInterface, } from './interfaces'
import { STANDARDS_REPORT_PRACTICE_SCOPE, STANDARDS_REPORT_MOMENTUM_SCORE_GOAL, buildStateStandardOptions, } from './shared'
import { AllSkillCategoriesAndSubCategoriesProvider } from './all_skill_categories_and_sub_categories';
import ReportStandardsModal from './report_standards_modal';

import { ReportPremiumProvider } from '../shared/report_premium_context';
import MomentumScoreGoalModal from '../shared/momentum_score_goal_modal';
import { NO_CLASSROOM, ALL_PRACTICE, PREMIUM_STATE_PAID, PREMIUM_STATE_LOCKED, PREMIUM_STATE_TRIAL, PREMIUM_STATE_NONE, CLASS_PRACTICE, smartGroupIllustrationSrc, } from '../shared/shared'
import VerificationBanner from '../verification/verification_banner';
import { SMALL_ICON_BASE_SRC, getPageTitle, Snackbar, Spinner, defaultSnackbarTimeout, MobileSidebarButtonContainer, TEACHER, ADMIN, APPROVED, HIGHER_ED_OR_INTERNATIONAL_SCHOOL, } from '../../../../Shared/index'
import useHideFooter from '../../../../Shared/hooks/useHideFooter'
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor'
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from '../progress_report_constants';
import { requestGet, requestPost, requestPut, } from '../../../../../modules/request'

const openSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-open.svg`
const closeSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-close.svg`

interface StandardsReportProps {
  adminVerificationStatus: string;
  allSkillSubCategories: SkillCategoryInterface[];
  allStateStandardsFrameworks: StateStandardsFrameworkInterface[];
  alternativeSchoolNames: string[];
  classrooms: ClassroomInterface[];
  needsEmailVerification: boolean;
  premiumState: typeof PREMIUM_STATE_PAID | typeof PREMIUM_STATE_TRIAL | typeof PREMIUM_STATE_LOCKED | typeof PREMIUM_STATE_NONE;
  role: string;
  school: any;
  schoolAdmins: any[];
  teacherVerificationStatus: string;
  hasSchoolOrDistrictPremium: boolean;
  defaultStateStandardsFramework: StateStandardsFrameworkInterface;
  hasSeenReportStandardsModal: boolean;
}

const StandardsReport = ({
  adminVerificationStatus,
  allSkillSubCategories,
  allStateStandardsFrameworks,
  alternativeSchoolNames,
  classrooms,
  needsEmailVerification,
  premiumState,
  role,
  school,
  schoolAdmins,
  defaultStateStandardsFramework,
  teacherVerificationStatus,
  hasSchoolOrDistrictPremium,
  hasSeenReportStandardsModal,
}: StandardsReportProps) => {
  document.title = getPageTitle("View Reports", "Standards Report")

  const [allSkillCategories, setAllSkillCategories] = React.useState<SkillCategoryInterface[]>([])
  const [allStateStandards, setAllStateStandards] = React.useState<StateStandardInterface[]>([])
  const [loadingStandardsData, setLoadingStandardsData] = React.useState<boolean>(!!classrooms.length)
  const [filteredSkillCategories, setFilteredSkillCategories] = React.useState([])
  const [filteredStateStandards, setFilteredStateStandards] = React.useState([])
  const [classroom, setClassroom] = React.useState(null)
  const [showSidebar, setShowSidebar] = React.useState(true)
  const [showMobileSidebar, setShowMobileSidebar] = React.useState(false)
  const [practiceScope, setPracticeScope] = React.useState<string>(window.localStorage.getItem(STANDARDS_REPORT_PRACTICE_SCOPE) || CLASS_PRACTICE);
  const [showMomentumScoreGoalModal, setShowMomentumScoreGoalModal] = React.useState(false)
  const [showReportStandardsModal, setShowReportStandardsModal] = React.useState(!hasSeenReportStandardsModal)
  const [momentumScoreGoal, setMomentumScoreGoal] = React.useState(window.localStorage.getItem(STANDARDS_REPORT_MOMENTUM_SCORE_GOAL) ? Number(window.localStorage.getItem(STANDARDS_REPORT_MOMENTUM_SCORE_GOAL)) : 80)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarText, setSnackbarText] = React.useState('')

  const [manuallySetSelectedSkillCategoryId, setManuallySetSelectedSkillCategoryId] = React.useState<boolean>(false)

  const [selectedSkillCategoryId, setSelectedSkillCategoryId] = React.useState<any>(null);
  const [selectedStateStandardId, setSelectedStateStandardId] = React.useState<any>(null);
  const [selectedStudentId, setSelectedStudentId] = React.useState<any>(null);
  const [selectedGradeLevel, setSelectedGradeLevel] = React.useState<string | null>(null);
  const [selectedStateStandardsFramework, setSelectedStateStandardsFramework] = React.useState<StateStandardsFrameworkInterface>(defaultStateStandardsFramework)

  // Data state for student and skill performance
  const [studentData, setStudentData] = React.useState<{[key: string]: StudentPerformanceData[]}>({})
  const [stateStandardsData, setStateStandardsData] = React.useState<{[key: string]: StateStandardPerformanceData[]}>({})
  const [studentDataKey, setStudentDataKey] = React.useState<string>('')
  const [stateStandardsDataKey, setStateStandardsDataKey] = React.useState<string>('')

  const navigate = useNavigate();
  const location = useLocation();

  const isByStudent = location.pathname.includes('by_student');
  const isByStandard = location.pathname.includes('by_standard');

  useHideFooter()
  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const showLockedPage = ![PREMIUM_STATE_PAID, PREMIUM_STATE_TRIAL].includes(premiumState)
  const userNeedsVerification = needsEmailVerification || (role === TEACHER && teacherVerificationStatus !== APPROVED) || (role === ADMIN && adminVerificationStatus && adminVerificationStatus !== APPROVED)
  const noClassroom = !classroom?.id || classroom.id === NO_CLASSROOM.id

  React.useEffect(() => {
    const localStorageSelectedClassroomId = window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID)
    const classroomFromLocalStorageClassroomId = classrooms.find(c => Number(c.id) === Number(localStorageSelectedClassroomId))
    setClassroom(classroomFromLocalStorageClassroomId || classrooms[0] || NO_CLASSROOM)
  }, [])

  React.useEffect(() => {
    if (!classroom) { return }
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, classroom.id)
  }, [classroom])

  React.useEffect(() => {
    window.localStorage.setItem(STANDARDS_REPORT_PRACTICE_SCOPE, practiceScope)
  }, [practiceScope])

  React.useEffect(() => {
    window.localStorage.setItem(STANDARDS_REPORT_MOMENTUM_SCORE_GOAL, momentumScoreGoal)
  }, [momentumScoreGoal])

  // Helper to get a valid state standard selection, or null if none available
  const getValidStateStandardId = React.useCallback((
    standards: StateStandardInterface[],
    currentClassroom: ClassroomInterface | null,
    gradeLevel: string | null,
    currentSelectionId: any
  ) => {
    if (!currentClassroom) return null;

    const options = buildStateStandardOptions(standards, currentClassroom, gradeLevel);
    if (options.length === 0) return null;

    // If current selection is valid (exists and is enabled), keep it
    const currentOption = options.find(opt => opt.value === currentSelectionId);
    if (currentOption && !currentOption.isDisabled) return currentSelectionId;

    // Otherwise, return first enabled option
    const firstEnabled = options.find(opt => !opt.isDisabled);
    return firstEnabled?.value ?? options[0]?.value ?? null;
  }, []);

  // Fetch standards data when framework changes
  React.useEffect(() => {
    if (!selectedStateStandardsFramework?.id || noClassroom) return;

    setLoadingStandardsData(true)
    const url = `/standards_reports/standards_data?state_standards_framework_id=${selectedStateStandardsFramework.id}`
    requestGet(url, (body: { allStateStandards: StateStandardInterface[], allSkillCategories: SkillCategoryInterface[] }) => {
      setAllStateStandards(body.allStateStandards)
      setAllSkillCategories(body.allSkillCategories)
      setLoadingStandardsData(false)
    })
  }, [selectedStateStandardsFramework?.id, classroom?.id])

  // Compute filtered lists when source data or grade level changes
  React.useEffect(() => {
    let newFilteredSkillCategories = allSkillCategories || []
    let newFilteredStateStandards = allStateStandards || []

    if (selectedGradeLevel) {
      newFilteredSkillCategories = newFilteredSkillCategories.filter(category =>
        category.grades?.includes(selectedGradeLevel)
      )
      newFilteredStateStandards = newFilteredStateStandards.filter(stateStandard =>
        stateStandard?.grades.includes(selectedGradeLevel)
      )
    } else {
      newFilteredSkillCategories = newFilteredSkillCategories.filter(skillCategory =>
        skillCategory.grades?.includes(classroom?.grade) ||
        newFilteredStateStandards.some(standard =>
          standard.l2_concepts?.includes(skillCategory.name)
        )
      )
    }

    setFilteredSkillCategories(newFilteredSkillCategories)
    setFilteredStateStandards(newFilteredStateStandards)
  }, [selectedGradeLevel, allSkillCategories, allStateStandards, classroom?.grade])

  // Handle tab-specific state and ensure valid selections
  React.useEffect(() => {
    if (isByStudent) {
      // Ensure we have a valid state standard selection
      if (classroom && filteredStateStandards.length > 0) {
        const validId = getValidStateStandardId(filteredStateStandards, classroom, selectedGradeLevel, selectedStateStandardId);
        if (validId !== selectedStateStandardId) {
          setSelectedStateStandardId(validId);
        }
      }

      // Clear student selection on By Student page
      if (selectedStudentId !== null) {
        setSelectedStudentId(null);
      }
    } else if (isByStandard) {
      // Clear state standard selection on By Standard page
      if (selectedStateStandardId !== null) {
        setSelectedStateStandardId(null);
      }

      if (!manuallySetSelectedSkillCategoryId) {
        setSelectedSkillCategoryId(null)
      }
    }

    // Handle skill category becoming invalid
    if (selectedSkillCategoryId && !filteredSkillCategories.find(sc => sc.id === selectedSkillCategoryId)) {
      setSelectedSkillCategoryId(null)
      setManuallySetSelectedSkillCategoryId(false)
    }
  }, [isByStudent, isByStandard, filteredStateStandards, filteredSkillCategories, classroom, selectedGradeLevel, selectedStateStandardId, selectedSkillCategoryId, selectedStudentId, manuallySetSelectedSkillCategoryId, getValidStateStandardId])

  React.useEffect(() => {
    if (!selectedStudentId) { return }

    setSelectedStudentId(null)
  }, [classroom])

  React.useEffect(() => {
    if (location.pathname === '/teachers/progress_reports/standards_report' && classroom?.id) {
      navigate(`/teachers/progress_reports/standards_report/classroom/${classroom?.id}/smart_groups/by_standard`);
    }
  }, [location.pathname, navigate, classroom?.id]);

  // Fetch student performance data - only when on by_student tab
  React.useEffect(() => {
    if (noClassroom || showLockedPage || !isByStudent) return;
    if (!selectedStateStandardId) return;

    const queryHash = {
      state_standard_id: selectedStateStandardId,
      classroom_id: classroom.id,
      all_history: practiceScope === ALL_PRACTICE,
      state_standards_framework_id: selectedStateStandardsFramework.id
    };

    const newStudentDataKey = JSON.stringify(queryHash);

    // Skip fetch if we already have this data
    if (studentData[newStudentDataKey]) {
      setStudentDataKey(newStudentDataKey);
      return;
    }

    setStudentDataKey(newStudentDataKey);

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: '/standards_reports/performance_breakdown_by_student',
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body: { data: StudentPerformanceData[] }) => {
      setStudentData(prevData => ({...prevData, [newStudentDataKey]: body.data}));
    });
  }, [classroom?.id, selectedStateStandardId, practiceScope, selectedStateStandardsFramework, isByStudent]);

  // Fetch standard performance data - only when on by_standard tab
  React.useEffect(() => {
    if (noClassroom || showLockedPage || !isByStandard) return;

    const queryHash = {
      concept_id: selectedSkillCategoryId,
      classroom_id: classroom.id,
      all_history: practiceScope === ALL_PRACTICE,
      user_id: selectedStudentId,
      state_standards_framework_id: selectedStateStandardsFramework.id
    };

    const newStateStandardsDataKey = JSON.stringify(queryHash);

    // Skip fetch if we already have this data
    if (stateStandardsData[newStateStandardsDataKey]) {
      setStateStandardsDataKey(newStateStandardsDataKey);
      return;
    }

    setStateStandardsDataKey(newStateStandardsDataKey);

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: '/standards_reports/performance_breakdown_by_standard',
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body: { data: StateStandardPerformanceData[] }) => {
      setStateStandardsData(prevData => ({...prevData, [newStateStandardsDataKey]: body.data}));
    });
  }, [classroom?.id, selectedSkillCategoryId, practiceScope, selectedStudentId, selectedStateStandardsFramework, isByStandard]);
  
  function handleCloseMomentumScoreGoalModal(snackbarMessage?: string) {
    setShowMomentumScoreGoalModal(false)
    if (typeof snackbarMessage === 'string') {
      setSnackbarText(snackbarMessage)
      setShowSnackbar(true)
    }
  }

  function handleCloseReportStandardsModal(snackbarMessage?: string) {
    setShowReportStandardsModal(false)
    if (!hasSeenReportStandardsModal) {
      requestPost('/milestones/complete_see_report_standards_modal', {}, () => {})
    }
    if (typeof snackbarMessage === 'string') {
      setSnackbarText(snackbarMessage)
      setShowSnackbar(true)
    }
  }

  function handleChangeSelectedStateStandardsFramework(newSelectedStateStandardsFramework: StateStandardsFrameworkInterface) {
    setSelectedStateStandardsFramework(newSelectedStateStandardsFramework)
    setSelectedSkillCategoryId(null)
    setSelectedStateStandardId(null)
    setSelectedStudentId(null)
    setSelectedGradeLevel(null)
    setManuallySetSelectedSkillCategoryId(false)
    setFilteredSkillCategories([])
    setFilteredStateStandards([])
    requestPut('/teacher_infos', { state_standards_framework_id: newSelectedStateStandardsFramework.id, })
  }

  function openMobileSidebar() { setShowMobileSidebar(true) }

  function closeMobileSidebar() { setShowMobileSidebar(false) }

  function toggleSidebar() { setShowSidebar(!showSidebar) }

  function switchToClassPractice() {
    setPracticeScope(CLASS_PRACTICE)
  }

  function setSkillCategoryById(categoryId: number | null) {
    setManuallySetSelectedSkillCategoryId(!!categoryId)
    setSelectedSkillCategoryId(categoryId);
    setSelectedStateStandardId(null);
  }

  function handleSetSelectedSkillCategoryId(skillCategory: any) {
    setSkillCategoryById(skillCategory.value);
  }

  function handleSetSelectedStateStandardId(stateStandard: any) {
    setSelectedStateStandardId(stateStandard.value);
  }

  function handleSetSelectedStudentId(student: any) {
    setSelectedStudentId(student.value);
  }

  function handleSetSelectedGradeLevel(grade: any) {
    setSelectedGradeLevel(grade.value);
  }

  function handleSkillCategoryClick(skillCategoryId: number) {
    setSkillCategoryById(skillCategoryId);
  }

  function handleStudentClick(studentId: number | string) {
    setSelectedStudentId(Number(studentId));
    // Navigate to by_standard view when a student is clicked
    const currentPath = location.pathname;
    navigate(currentPath.replace('/by_student', '/by_standard'));
  }

  function renderShowSidebarButton() {
    const ariaLabel = showSidebar ? 'Close sidebar' : 'Open sidebar'

    const imgSrc = showSidebar ? closeSidebarIconSrc : openSidebarIconSrc

    return (
      <button
        aria-expanded={showSidebar}
        aria-label={ariaLabel}
        className='interactive-wrapper focus-on-light show-sidebar-button'
        onClick={toggleSidebar}
        type="button"
      >
        <img alt="" src={imgSrc} />
      </button>
    )
  }

  let sidebar: React.ReactElement | null = null

  if (showSidebar || showMobileSidebar) {
    sidebar = (
      <Sidebar
        classrooms={classrooms}
        closeMobileSidebar={closeMobileSidebar}
        selectedClassroomId={classroom?.id}
        setSelectedClassroom={setClassroom}
        showMobileSidebar={showMobileSidebar}
        showSidebarButton={renderShowSidebarButton()}
      />
    )
  }

  function renderNeedsVerificationEmptyState() {
    return (
      <section className="empty-state">
        <h2>Get verified to see your students’ full practice history</h2>
        <p>After completing the steps above to get verified, you will unlock each student’s full practice history across all teachers and classes for a comprehensive view of their progress and growth. In the meantime, you can access Class Practice.</p>
        <button className="quill-button green contained medium focus-on-light" onClick={switchToClassPractice} type="button">Switch to Class Practice</button>
      </section>
    )
  }

  function buildAdminContactMailto() {
    const subject = "Can you help unlock Quill's full Standards Report?";

    let toEmail = "";

    if (schoolAdmins?.length === 1) {
      toEmail = schoolAdmins[0].email || "";
    }

    const body = `Hi there,

I’ve been using Quill’s new Standards Report, and it’s already giving me helpful insight into how my students are performing on standards. With a School or District Premium plan, the report can do even more, like showing each student’s complete standards-aligned performance across all available practice.

That broader view would make it much easier to understand performance across standards and better support each student’s growth. You can see the full list of Premium features here: https://www.quill.org/premium.

There’s more about the Standards Report in this article: https://support.quill.org/en/articles/13289285-new-standards-report.

If you'd like to learn more or explore pricing options, you can fill out this short form to connect with Quill's Partnerships Team: https://quillorg.salesmate.io/webforms/#/5d6a5b51-8ed9-49d5-923e-dcab1baa0063

Thanks for considering it!`;

    return `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function renderUpgradeEmptyState() {
    return (
      <section className="empty-state upgrade-empty-state">
        <img alt="" src={smartGroupIllustrationSrc} />
        <h2>See the Full Story of Each Student’s Journey</h2>
        <p>Go beyond the work in just your class. The All Practice view unlocks a student’s complete standards-aligned practice history, including work from previous years and other teachers, to give you a clear, day-one view of their performance on each standard.</p>
        <p><b>Upgrading to a School or District Premium plan unlocks the full power of this report.</b> You’ll gain the ability to see the complete, year-over-year performance on standards for every student. You’ll also unlock the power to turn those insights into instructional decisions with confidence.</p>
        <div className="buttons-wrapper">
          <a className="quill-button green outlined medium focus-on-light" href='/premium' rel="noopener noreferrer" target="_blank">Learn more about School & District Premium</a>
          <a className="quill-button green contained medium focus-on-light" href={buildAdminContactMailto()}>Contact your admin</a>
        </div>
      </section>
    )
  }

  function renderHeader(title: string) {
    const filteredGradeLevels = Array.from(
      new Set(
        allStateStandards
          .flatMap(standard => standard.grades || [])
          .filter(Boolean)
      )
    ).sort();

    return (
      <React.Fragment>
        <StandardsReportHeader
          byStudent={location.pathname.includes('by_student')}
          selectedSkillCategory={allSkillCategories.find(skillCategory => skillCategory.id === selectedSkillCategoryId)}
          selectedStateStandard={allStateStandards.find(standard => standard.id === selectedStateStandardId)}
          selectedStateStandardsFramework={selectedStateStandardsFramework}
          setShowMomentumScoreGoalModal={setShowMomentumScoreGoalModal}
          setShowReportStandardsModal={setShowReportStandardsModal}
          title={title}
        />
        <MobileSidebarButtonContainer openMobileSidebar={openMobileSidebar} />
        <StandardsReportSubheader
          allSkillCategories={allSkillCategories}
          byStudent={location.pathname.includes('by_student')}
          classroom={classroom}
          filteredGradeLevels={filteredGradeLevels}
          filteredSkillCategories={filteredSkillCategories}
          filteredStateStandards={filteredStateStandards}
          onGradeLevelChange={handleSetSelectedGradeLevel}
          onSkillCategoryChange={handleSetSelectedSkillCategoryId}
          onStateStandardChange={handleSetSelectedStateStandardId}
          onStudentChange={handleSetSelectedStudentId}
          practiceScope={practiceScope}
          selectedGradeLevel={selectedGradeLevel}
          selectedSkillCategoryId={selectedSkillCategoryId}
          selectedStateStandardId={selectedStateStandardId}
          selectedStudentId={selectedStudentId}
          setPracticeScope={setPracticeScope}
        />
      </React.Fragment>
    );
  }

  function renderVerificationBanner() {
    const isHigherEdOrInternationSchoolAndAllPractice = school && HIGHER_ED_OR_INTERNATIONAL_SCHOOL.includes(school.name) && practiceScope === ALL_PRACTICE

    const shouldShowVerificationBanner =
      isHigherEdOrInternationSchoolAndAllPractice ||
      (userNeedsVerification &&
        practiceScope !== CLASS_PRACTICE &&
        hasSchoolOrDistrictPremium)

    if (!shouldShowVerificationBanner) return null

    return (
      <VerificationBanner
        adminVerificationStatus={adminVerificationStatus}
        alternativeSchoolNames={alternativeSchoolNames}
        needsEmailVerification={needsEmailVerification}
        role={role}
        school={school}
        schoolAdmins={schoolAdmins}
        teacherVerificationStatus={teacherVerificationStatus}
      />
    )
  }

  if (showLockedPage) {
    return (
      <LockedPage
        premiumState={premiumState}
      />
    )
  }


  const sharedProps = {
    filteredSkillCategories,
    filteredStateStandards,
    classroom,
    momentumScoreGoal,
    onSkillCategoryClick: handleSkillCategoryClick,
    onStudentChange: handleSetSelectedStudentId,
    onStudentClick: handleStudentClick,
    practiceScope,
    renderHeader,
    renderNeedsVerificationEmptyState,
    renderVerificationBanner,
    renderUpgradeEmptyState,
    selectedGradeLevel,
    selectedSkillCategoryId,
    selectedStateStandardId,
    selectedStudentId,
    stateStandardsData,
    stateStandardsDataKey,
    studentData,
    studentDataKey,
    userNeedsVerification,
  }

  return (
    <ReportPremiumProvider hasSchoolOrDistrictPremium={hasSchoolOrDistrictPremium}>
      <div className="standards-report-container">
        {showMomentumScoreGoalModal && (
          <MomentumScoreGoalModal
            closeModal={handleCloseMomentumScoreGoalModal}
            momentumScoreGoal={momentumScoreGoal}
            setMomentumScoreGoal={setMomentumScoreGoal}
          />
        )}
        {showReportStandardsModal && (
          <ReportStandardsModal
            allStateStandardsFrameworks={allStateStandardsFrameworks}
            closeModal={handleCloseReportStandardsModal}
            handleChangeSelectedStateStandardsFramework={handleChangeSelectedStateStandardsFramework}
            selectedStateStandardsFramework={selectedStateStandardsFramework}
          />
        )}
        {sidebar}
        <div className={showSidebar ? 'sidebar-open' : 'sidebar-closed'}>
          {showSidebar ? null : renderShowSidebarButton()}
          {loadingStandardsData ? (
            <div className="loading-spinner-container">
              <Spinner />
            </div>
          ) : (
            <AllSkillCategoriesAndSubCategoriesProvider allSkillCategories={allSkillCategories} allSkillSubCategories={allSkillSubCategories}>
              <Routes>
                <Route
                  element={<SmartGroups {...sharedProps} />}
                  path='/classroom/:classroomId/smart_groups/*'
                />
                <Route
                  element={<PerformanceBreakdown {...sharedProps} />}
                  path='/classroom/:classroomId/performance_breakdown/*'
                />
              </Routes>
            </AllSkillCategoriesAndSubCategoriesProvider>
          )}
        </div>
        <Snackbar text={snackbarText} visible={showSnackbar} />
      </div>
    </ReportPremiumProvider>
  )
}

export default StandardsReport
